import React, { useState, useEffect } from 'react';
import { AdminHeader } from './AdminLayout';
import { getGemeinden } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { 
    Download, 
    FileSpreadsheet, 
    Printer,
    Building2,
    ExternalLink,
    FileText,
    FileDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ExportTools() {
    const [gemeinden, setGemeinden] = useState([]);
    const [selectedGemeinde, setSelectedGemeinde] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchGemeinden = async () => {
            try {
                const response = await getGemeinden();
                setGemeinden(response.data);
            } catch (err) {
                console.error('Failed to fetch gemeinden:', err);
            }
        };
        fetchGemeinden();
    }, []);

    const handleCSVExport = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            const url = selectedGemeinde 
                ? `${API_URL}/api/export/angebote/csv?gemeinde_id=${selectedGemeinde}`
                : `${API_URL}/api/export/angebote/csv`;
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error('Export fehlgeschlagen');
            
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `angebote_export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
            
            toast.success('CSV erfolgreich exportiert');
        } catch (err) {
            console.error('Export failed:', err);
            toast.error('Export fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    };

    // A4 Landscape print view for a single Gemeinde - opens in new window
    const handlePrintGemeinde = async () => {
        if (!selectedGemeinde) {
            toast.error('Bitte wähle eine Gemeinde aus');
            return;
        }
        
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            const response = await fetch(
                `${API_URL}/api/export/print/gemeinde/${selectedGemeinde}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error('Laden fehlgeschlagen');
            
            const html = await response.text();
            
            // Open in new window for printing/PDF
            const printWindow = window.open('', '_blank', 'width=1200,height=850');
            printWindow.document.write(html);
            printWindow.document.close();
            
            toast.success('Druckansicht geöffnet');
        } catch (err) {
            console.error('Print preview failed:', err);
            toast.error('Druckvorschau konnte nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    // Download PDF directly
    const handleDownloadPDF = async () => {
        if (!selectedGemeinde) {
            toast.error('Bitte wähle eine Gemeinde aus');
            return;
        }
        
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            const response = await fetch(
                `${API_URL}/api/export/print/gemeinde/${selectedGemeinde}?format=pdf`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error('PDF-Download fehlgeschlagen');
            
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `Angebote_${selectedGemeindeName}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
            
            toast.success('PDF heruntergeladen');
        } catch (err) {
            console.error('PDF download failed:', err);
            toast.error('PDF-Download fehlgeschlagen');
        } finally {
            setLoading(false);
        }
    };

    // All offers print view (all Gemeinden)
    const handlePrintAll = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            const response = await fetch(
                `${API_URL}/api/export/angebote/print`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error('Laden fehlgeschlagen');
            
            const html = await response.text();
            
            // Open in new window for printing
            const printWindow = window.open('', '_blank', 'width=1200,height=800');
            printWindow.document.write(html);
            printWindow.document.close();
            
            toast.success('Druckansicht geöffnet');
        } catch (err) {
            console.error('Print all failed:', err);
            toast.error('Druckvorschau konnte nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const selectedGemeindeName = gemeinden.find(g => g.id === selectedGemeinde)?.name;

    return (
        <>
            <AdminHeader title="Export & Druck" />

            <div className="admin-content">
                <div className="max-w-4xl space-y-8">
                    
                    {/* A4 Schaukasten Print - Primary Feature */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-12 h-12 rounded-xl bg-[#cf2e2e]/10 flex items-center justify-center">
                                <Printer className="h-6 w-6 text-[#cf2e2e]" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold">Angebote Drucken</h2>
                                <p className="text-sm text-muted-foreground">
                                    Angebote einer Gemeinde als A4-Querformat für den Schaukasten
                                </p>
                            </div>
                        </div>
                        
                        {/* Gemeinde Selection - clearly visible */}
                        <div className="bg-muted/40 rounded-xl p-4 mb-5">
                            <label className="block text-sm font-medium mb-2">
                                Gemeinde auswählen
                            </label>
                            <div className="flex items-center gap-3">
                                <Building2 className="h-5 w-5 text-muted-foreground" />
                                <Select value={selectedGemeinde} onValueChange={setSelectedGemeinde}>
                                    <SelectTrigger className="w-64 bg-background" data-testid="gemeinde-select">
                                        <SelectValue placeholder="Gemeinde wählen..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {gemeinden.map(g => (
                                            <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                            <Button 
                                onClick={handleDownloadPDF} 
                                disabled={loading || !selectedGemeinde}
                                className="rounded-xl"
                                data-testid="download-pdf-btn"
                            >
                                <FileDown className="h-4 w-4 mr-2" />
                                {selectedGemeinde 
                                    ? `${selectedGemeindeName} als PDF` 
                                    : 'Gemeinde wählen...'}
                            </Button>
                            <Button 
                                onClick={handlePrintGemeinde} 
                                disabled={loading || !selectedGemeinde}
                                variant="outline"
                                className="rounded-xl"
                                data-testid="print-gemeinde-btn"
                            >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Vorschau öffnen
                            </Button>
                        </div>
                    </div>

                    {/* CSV Export */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">CSV Export</h2>
                                <p className="text-sm text-muted-foreground">
                                    Alle Angebote als Tabelle exportieren (für Excel/Numbers)
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex gap-4">
                            <Button 
                                onClick={handleCSVExport} 
                                disabled={loading}
                                variant="outline"
                                className="rounded-xl"
                                data-testid="csv-export-btn"
                            >
                                <Download className="h-4 w-4 mr-2" />
                                {selectedGemeinde ? `${selectedGemeindeName} exportieren` : 'Alle exportieren'}
                            </Button>
                        </div>
                    </div>

                    {/* All Print View */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                                <FileText className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Gesamtübersicht drucken</h2>
                                <p className="text-sm text-muted-foreground">
                                    Alle Angebote aller Gemeinden als Druckansicht
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex gap-4">
                            <Button 
                                onClick={handlePrintAll} 
                                disabled={loading}
                                variant="outline"
                                className="rounded-xl"
                                data-testid="print-all-btn"
                            >
                                <Printer className="h-4 w-4 mr-2" />
                                Alle Angebote drucken
                            </Button>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
}
